import { useEffect, useId, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { CircleHelp, X } from 'lucide-react'
import { MarkdownContent } from '@/features/ai/MarkdownContent'
import { resolvePageHelp, type PageHelpEntry } from '@/help/pageHelp'
import { IconButton } from '@/components/IconButton'

type PageHelpProps = {
  /** Override route lookup (e.g. \`ai-assist\`). */
  helpKey?: string
  className?: string
}

export function PageHelp({ helpKey, className = '' }: PageHelpProps) {
  const location = useLocation()
  const entry = resolvePageHelp(helpKey ?? location.pathname)
  const [open, setOpen] = useState(false)
  const panelId = useId()
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setOpen(false)
  }, [location.pathname, helpKey])

  useEffect(() => {
    if (!open) return
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }
    function onPointer(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onPointer)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onPointer)
    }
  }, [open])

  if (!entry) return null

  return (
    <div
      ref={rootRef}
      className={`page-help${className ? ` ${className}` : ''}${open ? ' page-help--open' : ''}`}
    >
      <IconButton
        label={`Help: ${entry.title}`}
        variant="ghost"
        className="page-help__trigger"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((value) => !value)}
      >
        <CircleHelp size={18} strokeWidth={2} />
      </IconButton>
      {open ? <PageHelpPanel id={panelId} entry={entry} onClose={() => setOpen(false)} /> : null}
    </div>
  )
}

function PageHelpPanel({
  id,
  entry,
  onClose,
}: {
  id: string
  entry: PageHelpEntry
  onClose: () => void
}) {
  return (
    <div id={id} className="page-help__panel" role="dialog" aria-label={entry.title}>
      <header className="page-help__header">
        <h2 className="page-help__title">{entry.title}</h2>
        <IconButton label="Close help" variant="ghost" onClick={onClose}>
          <X size={16} strokeWidth={2} />
        </IconButton>
      </header>
      <MarkdownContent className="page-help__body">{entry.body}</MarkdownContent>
    </div>
  )
}
